import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useForm, zodResolver, UseFormReturn } from '@fiverr-private/forms';
import { useIdentificationContext } from '@fiverr-private/identification_core';
import { ProfileSetupSchema, createProfileSetupSchema } from '../../ProfileSetupStep.constants';
import { validateUsernameForProfileSetup } from '../../../../services/validation/validateUsernameForProfileSetup';
import { translate } from '../../../../shared/utils/translate';
import { VALIDATION_DEBOUNCE_TIME } from './useProfileSetupForm.constants';

interface ValidateArgs {
    value: string;
    methods: UseFormReturn<ProfileSetupSchema>;
}

export const useProfileSetupForm = () => {
    const schema = useMemo(() => createProfileSetupSchema(), []);
    const [loading, setLoading] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState(false);
    const { isSocialFlow, username: initialUsername } = useIdentificationContext();

    const methods = useForm<ProfileSetupSchema>({
        mode: 'onSubmit',
        resolver: zodResolver(schema as any),
        defaultValues: {
            username: isSocialFlow ? initialUsername : '',
        },
    });

    const verifyUsernameDebounced = useMemo(() => {
        const verifyUsername = async ({ value, methods }: ValidateArgs) => {
            setLoading(true);
            setUsernameAvailable(false);

            const usernameFormatValid = await methods.trigger('username');
            if (!usernameFormatValid) {
                setLoading(false);
                return;
            }

            const { errorKeys } = await validateUsernameForProfileSetup(value);
            setLoading(false);

            if (errorKeys?.username?.length) {
                methods.setError('username', {
                    message: translate(`validation.username.${errorKeys.username[0]}`),
                });
                return;
            }

            methods.clearErrors('username');
            setUsernameAvailable(true);
        };

        return debounce(verifyUsername, VALIDATION_DEBOUNCE_TIME);
    }, []);

    useEffect(() => {
        const cleanupOnUnmount = () => verifyUsernameDebounced.cancel();
        return cleanupOnUnmount;
    }, [verifyUsernameDebounced]);

    const onUsernameChange = async (value: string) => {
        verifyUsernameDebounced.cancel();
        methods.clearErrors('username');
        setLoading(true);
        setUsernameAvailable(false);

        await verifyUsernameDebounced({ value, methods });
    };

    return {
        loading,
        setLoading,
        usernameAvailable,
        methods,
        onUsernameChange,
    };
};
